import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { 
  Calendar, MapPin, Clock, UtensilsCrossed, Lightbulb, 
  Share2, Download, Phone, Loader2, AlertCircle, Bed,
  CheckCircle, ArrowRight, Coffee, Mountain, Waves, 
  Building, Users, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SEOHead } from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Curated images for Koraput attractions from Pexels/Unsplash
const ATTRACTION_IMAGES = {
  // Mountain peak with valley view (similar to Deomali)
  deomali: 'https://images.pexels.com/photos/34384516/pexels-photo-34384516.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Waterfall in India/Odisha region
  duduma: 'https://images.pexels.com/photos/18303336/pexels-photo-18303336.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Lake/reservoir scenic view
  kolab: 'https://images.pexels.com/photos/34384516/pexels-photo-34384516.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Jagannath Temple Odisha style architecture
  jagannath: 'https://images.unsplash.com/photo-1629723248038-8b3bc1ecce29?w=800&q=80',
  
  // Tribal market in Odisha - woman selling vegetables
  tribal: 'https://images.pexels.com/photos/29740756/pexels-photo-29740756.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Coffee beans/plantation
  coffee: 'https://images.unsplash.com/photo-1580933073521-dc49ac0d4e6a?w=800&q=80',
  
  // Luxury hotel
  hotel: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
  
  // Trekking/hiking scenic
  trek: 'https://images.pexels.com/photos/18991748/pexels-photo-18991748.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Nature/forest greenery
  nature: 'https://images.pexels.com/photos/18303336/pexels-photo-18303336.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Sunrise over mountains
  sunrise: 'https://images.pexels.com/photos/34384516/pexels-photo-34384516.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Village/tribal elderly woman
  village: 'https://images.pexels.com/photos/27025223/pexels-photo-27025223.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Indian thali food
  food: 'https://images.pexels.com/photos/5775684/pexels-photo-5775684.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Spa relaxation
  spa: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
  
  // Boating on reservoir
  boat: 'https://images.pexels.com/photos/34384516/pexels-photo-34384516.jpeg?auto=compress&cs=tinysrgb&w=800',
  
  // Default nature scene
  default: 'https://images.pexels.com/photos/18303336/pexels-photo-18303336.jpeg?auto=compress&cs=tinysrgb&w=800',
};

// Smart image matcher based on activity text
const getActivityImage = (activity, imageKey) => {
  // First check if a specific image key is provided
  if (imageKey && ATTRACTION_IMAGES[imageKey]) {
    return ATTRACTION_IMAGES[imageKey];
  }
  
  const text = activity.toLowerCase();
  
  // Keyword matching for better images
  if (text.includes('deomali') || text.includes('peak') || text.includes('highest')) return ATTRACTION_IMAGES.deomali;
  if (text.includes('duduma') || text.includes('waterfall') || text.includes('falls')) return ATTRACTION_IMAGES.duduma;
  if (text.includes('kolab') || text.includes('reservoir') || text.includes('dam') || text.includes('lake')) return ATTRACTION_IMAGES.kolab;
  if (text.includes('jagannath') || text.includes('temple') || text.includes('heritage')) return ATTRACTION_IMAGES.jagannath;
  if (text.includes('tribal') || text.includes('market') || text.includes('village') || text.includes('haat')) return ATTRACTION_IMAGES.tribal;
  if (text.includes('coffee') || text.includes('plantation')) return ATTRACTION_IMAGES.coffee;
  if (text.includes('check-in') || text.includes('check-out') || text.includes('hotel') || text.includes('room')) return ATTRACTION_IMAGES.hotel;
  if (text.includes('trek') || text.includes('hike') || text.includes('climb')) return ATTRACTION_IMAGES.trek;
  if (text.includes('sunrise') || text.includes('dawn') || text.includes('morning view')) return ATTRACTION_IMAGES.sunrise;
  if (text.includes('boat') || text.includes('boating') || text.includes('cruise')) return ATTRACTION_IMAGES.boat;
  if (text.includes('spa') || text.includes('massage') || text.includes('relax') || text.includes('wellness')) return ATTRACTION_IMAGES.spa;
  if (text.includes('dinner') || text.includes('lunch') || text.includes('breakfast') || text.includes('cuisine') || text.includes('food')) return ATTRACTION_IMAGES.food;
  if (text.includes('forest') || text.includes('nature') || text.includes('garden')) return ATTRACTION_IMAGES.nature;
  
  return ATTRACTION_IMAGES.default;
};

const getActivityIcon = (activity) => {
  const lower = activity.toLowerCase();
  if (lower.includes('trek') || lower.includes('peak') || lower.includes('mountain')) return Mountain;
  if (lower.includes('water') || lower.includes('reservoir') || lower.includes('boat')) return Waves;
  if (lower.includes('coffee') || lower.includes('breakfast')) return Coffee;
  if (lower.includes('temple') || lower.includes('heritage')) return Building;
  if (lower.includes('tribal') || lower.includes('market') || lower.includes('village')) return Users;
  if (lower.includes('photo') || lower.includes('view')) return Camera;
  if (lower.includes('hotel') || lower.includes('check')) return Bed;
  return MapPin;
};

export default function ItineraryPage() {
  const { id } = useParams();
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryImages, setGalleryImages] = useState({});
  const [contact, setContact] = useState({
    phones: ['+91 6372 350 065'],
    whatsapp: '+916372350065'
  });

  useEffect(() => {
    // Fetch gallery images for place tags
    const fetchGalleryImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/gallery/place-images`);
        setGalleryImages(response.data || {});
      } catch (err) {
        console.warn('Failed to fetch gallery images:', err);
      }
    };
    fetchGalleryImages();
  }, []);

  // Get image from gallery or fallback to defaults
  const getImageForActivity = (activity, imageKey) => {
    const text = activity.toLowerCase();
    
    // Determine which tag to look for
    let tagToSearch = null;
    if (text.includes('deomali') || text.includes('peak') || text.includes('highest')) tagToSearch = 'deomali';
    else if (text.includes('duduma') || text.includes('waterfall') || text.includes('falls')) tagToSearch = 'duduma';
    else if (text.includes('kolab') || text.includes('reservoir') || text.includes('dam') || text.includes('lake')) tagToSearch = 'kolab';
    else if (text.includes('jagannath') || text.includes('temple') || text.includes('heritage')) tagToSearch = 'jagannath';
    else if (text.includes('tribal') || text.includes('market') || text.includes('village') || text.includes('haat')) tagToSearch = 'tribal';
    else if (text.includes('coffee') || text.includes('plantation')) tagToSearch = 'coffee';
    else if (text.includes('check-in') || text.includes('check-out') || text.includes('hotel') || text.includes('room')) tagToSearch = 'hotel';
    else if (text.includes('trek') || text.includes('hike') || text.includes('climb')) tagToSearch = 'trek';
    else if (text.includes('sunrise') || text.includes('dawn') || text.includes('morning view')) tagToSearch = 'sunrise';
    else if (text.includes('boat') || text.includes('boating') || text.includes('cruise')) tagToSearch = 'boat';
    else if (text.includes('spa') || text.includes('massage') || text.includes('relax') || text.includes('wellness')) tagToSearch = 'spa';
    else if (text.includes('dinner') || text.includes('lunch') || text.includes('breakfast') || text.includes('cuisine') || text.includes('food')) tagToSearch = 'food';
    else if (text.includes('forest') || text.includes('nature') || text.includes('garden')) tagToSearch = 'nature';
    
    // Check if we have gallery images for this tag
    if (tagToSearch && galleryImages[tagToSearch] && galleryImages[tagToSearch].length > 0) {
      // Pick a random image from gallery
      const randomIndex = Math.floor(Math.random() * galleryImages[tagToSearch].length);
      const imgSrc = galleryImages[tagToSearch][randomIndex].src;
      // Convert relative paths to absolute URLs
      if (imgSrc.startsWith('/')) {
        return `${API_URL}${imgSrc}`;
      }
      return imgSrc;
    }
    
    // Fallback to default images
    return getActivityImage(activity, imageKey);
  };

  useEffect(() => {
    // Fetch site settings for contact info
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        if (response.data?.contact) {
          setContact(prev => ({ ...prev, ...response.data.contact }));
        }
      } catch (err) {
        console.warn('Failed to fetch settings:', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchItinerary = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/itinerary/${id}`);
        setItinerary(response.data);
      } catch (err) {
        console.error('Failed to fetch itinerary:', err);
        setError('Itinerary not found or has expired.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchItinerary();
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: itinerary?.title || 'My Koraput Trip Itinerary',
          text: 'Check out my personalized trip itinerary from The Hotel Emerald!',
          url,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your personalized itinerary...</p>
        </div>
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="min-h-screen bg-background" data-testid="itinerary-error">
        <Navbar />
        <div className="container-custom py-32 text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">Itinerary Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/" className="btn-emerald inline-flex items-center gap-2">
            Return Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="itinerary-page">
      <SEOHead 
        title={`${itinerary.title} | The Hotel Emerald`}
        description={itinerary.summary}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 bg-emerald-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/gallery/hotel-6.jpeg" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative container-custom py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="bg-gold text-emerald-950 mb-4">Your Personalized Itinerary</Badge>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-4">{itinerary.title}</h1>
            <p className="text-emerald-200 text-lg mb-6">{itinerary.summary}</p>
            
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-2 bg-emerald-800/50 px-3 py-1.5 rounded-full">
                <Calendar className="w-4 h-4" />
                {itinerary.days?.length || 1} Days
              </span>
              <span className="flex items-center gap-2 bg-emerald-800/50 px-3 py-1.5 rounded-full">
                <MapPin className="w-4 h-4" />
                Koraput, Odisha
              </span>
              <span className="flex items-center gap-2 bg-emerald-800/50 px-3 py-1.5 rounded-full">
                Est. {itinerary.total_estimated_cost}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Share & Actions Bar */}
      <div className="sticky top-20 z-30 bg-background border-b">
        <div className="container-custom py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground hidden sm:block">
            Created just for you by Emerald AI
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Link to="/book">
              <Button size="sm" className="btn-emerald gap-2">
                <Phone className="w-4 h-4" />
                Book Now
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Day-by-Day Itinerary */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {itinerary.days?.map((day, dayIndex) => (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: dayIndex * 0.1 }}
                className="mb-12 last:mb-0"
              >
                {/* Day Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-serif text-xl">
                    {day.day}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">Day {day.day}</p>
                    <h2 className="font-serif text-xl sm:text-2xl text-foreground">{day.title}</h2>
                  </div>
                </div>

                {/* Activities Timeline */}
                <div className="ml-6 pl-6 border-l-2 border-emerald-200 dark:border-emerald-800 space-y-6">
                  {day.activities?.map((activity, actIndex) => {
                    const ActivityIcon = getActivityIcon(activity.activity);
                    const imageSrc = getImageForActivity(activity.activity, activity.image);
                    
                    return (
                      <div key={actIndex} className="relative">
                        {/* Timeline dot */}
                        <div className="absolute -left-[31px] w-4 h-4 rounded-full bg-emerald-600 border-4 border-background" />
                        
                        <div className="bg-card border rounded-xl overflow-hidden">
                          <div className="sm:flex">
                            <div className="sm:w-1/3 h-32 sm:h-auto relative overflow-hidden">
                              <img 
                                src={imageSrc} 
                                alt={activity.activity}
                                className="w-full h-full object-cover transition-transform hover:scale-105"
                                loading="lazy"
                              />
                            </div>
                            <div className="p-4 sm:w-2/3">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                <Clock className="w-4 h-4" />
                                {activity.time}
                              </div>
                              <h3 className="font-medium text-foreground flex items-center gap-2 mb-1">
                                <ActivityIcon className="w-4 h-4 text-emerald-600" />
                                {activity.activity}
                              </h3>
                              <p className="text-sm text-muted-foreground">{activity.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Dining Section */}
                  {day.dining && day.dining.length > 0 && (
                    <div className="relative">
                      <div className="absolute -left-[31px] w-4 h-4 rounded-full bg-gold border-4 border-background" />
                      
                      <div className="bg-gold/10 border border-gold/30 rounded-xl p-4">
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-3">
                          <UtensilsCrossed className="w-4 h-4 text-gold" />
                          Dining Recommendations
                        </h4>
                        <div className="space-y-2">
                          {day.dining.map((meal, mealIndex) => (
                            <div key={mealIndex} className="flex items-start gap-3 text-sm">
                              <span className="font-medium text-foreground min-w-[80px]">{meal.meal}:</span>
                              <div>
                                <span className="text-foreground">{meal.recommendation}</span>
                                {meal.description && (
                                  <span className="text-muted-foreground"> – {meal.description}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Tip */}
                  {day.tips && (
                    <div className="relative">
                      <div className="absolute -left-[31px] w-4 h-4 rounded-full bg-emerald-400 border-4 border-background" />
                      
                      <div className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl p-4">
                        <Lightbulb className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground">{day.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Room */}
      {itinerary.recommended_room && (
        <section className="py-12 bg-emerald-950 text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-8"
              >
                <p className="overline text-gold mb-2">Recommended For You</p>
                <h2 className="font-serif text-2xl sm:text-3xl">Your Perfect Room</h2>
              </motion.div>

              <div className="bg-emerald-900/50 rounded-xl overflow-hidden border border-emerald-800">
                <div className="md:flex">
                  <div className="md:w-2/5">
                    <img 
                      src={ATTRACTION_IMAGES.hotel}
                      alt={itinerary.recommended_room.name}
                      className="w-full h-48 md:h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:w-3/5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-serif text-xl mb-1">{itinerary.recommended_room.name}</h3>
                        <p className="text-2xl font-serif">
                          ₹{itinerary.recommended_room.price?.toLocaleString()}
                          <span className="text-sm text-emerald-300">/night</span>
                        </p>
                      </div>
                      <Badge className="bg-gold text-emerald-950">Best Match</Badge>
                    </div>
                    <p className="text-emerald-200 text-sm mb-4">{itinerary.recommended_room.reason}</p>
                    <Link to="/book" className="btn-gold inline-flex items-center gap-2">
                      Book This Room
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Trip Info Cards */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6">
            {/* Best Time to Visit */}
            <div className="bg-card border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-serif text-lg">Best Time to Visit</h3>
              </div>
              <p className="text-muted-foreground text-sm">{itinerary.best_time_to_visit}</p>
            </div>

            {/* Packing Tips */}
            <div className="bg-card border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-serif text-lg">Packing Tips</h3>
              </div>
              <ul className="space-y-2">
                {itinerary.packing_tips?.map((tip, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-900 to-emerald-800 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-2xl sm:text-3xl mb-4">Ready to Experience Koraput?</h2>
            <p className="text-emerald-200 mb-6 max-w-xl mx-auto">
              Book your stay at The Hotel Emerald and let us help you create unforgettable memories.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book" className="btn-gold inline-flex items-center justify-center gap-2">
                Book Your Stay
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href={`tel:${contact.phones?.[0]?.replace(/\s/g, '') || '+916372350065'}`}
                className="btn-emerald bg-white/10 hover:bg-white/20 inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Call {contact.phones?.[0] || '+91 6372 350 065'}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
